import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly THEME_KEY = 'userTheme';
  private isDarkMode$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.loadTheme();
  }

  /**
   * Load theme preference from localStorage and apply it
   */
  private loadTheme(): void {
    const savedTheme = localStorage.getItem(this.THEME_KEY);
    const isDark = savedTheme === 'dark';
    this.applyTheme(isDark);
    this.isDarkMode$.next(isDark);
  }

  /**
   * Apply theme by adding or removing the dark-theme class on body element
   */
  private applyTheme(isDark: boolean): void {
    if (isDark) {
      document.body.classList.add('dark-theme');
    } else {
      document.body.classList.remove('dark-theme');
    }
  }

  /**
   * Save theme preference to localStorage
   */
  private saveTheme(isDark: boolean): void {
    localStorage.setItem(this.THEME_KEY, isDark ? 'dark' : 'light');
  }

  /**
   * Toggle between light and dark theme
   */
  public toggleTheme(): void {
    const newTheme = !this.isDarkMode$.value;
    this.setTheme(newTheme);
  }

  /**
   * Set specific theme
   * @param isDark - true for dark theme, false for light theme
   */
  public setTheme(isDark: boolean): void {
    this.applyTheme(isDark);
    this.saveTheme(isDark);
    this.isDarkMode$.next(isDark);
  }

  /**
   * Get observable for theme changes
   * @returns Observable that emits true when dark mode is active, false otherwise
   */
  public getThemeObservable(): Observable<boolean> {
    return this.isDarkMode$.asObservable();
  }

  /**
   * Get current theme state synchronously
   * @returns true if dark mode is active, false otherwise
   */
  public isDark(): boolean {
    return this.isDarkMode$.value;
  }
}
